import { removeDiacritics, SelectMenuProps, SelectWrapper, theme } from '@arnold/common';
import { FieldProps } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OrganizationTeamsQuery } from '../../generated/hooks';
import CustomSupervisorTeamMenu from './CustomSupervisorTeamMenu';
import { MemberWithLeaderOfTeams, TeamsWithSubTeams } from './TeamsAndPeople';
import { components } from 'react-select';
import { SELECT_ALL_ID } from '../../components/Forms/RespondentForm/teamSelectionUtils';
import { useTranslation } from 'react-i18next';

type Team = NonNullable<OrganizationTeamsQuery['organizationTeams']>['0'];
export type TeamWithSubTeams = Team & {
  subTeams: TeamsWithSubTeams;
  numberOfEmployees: number;
  members: MemberWithLeaderOfTeams[];
};

interface Props extends FieldProps {
  teams: TeamsWithSubTeams;
  value?: string | string[];
  onChange?: (teamId: string | string[]) => void;
  topTeamName?: string;
  multiselect?: boolean;
  permanentlyEnabledTeamIds?: string[];
  filterMultiValueSelectedIndicators?: (id: string) => boolean;
  maxVisibleMultiValueCount?: number;
  isDisabled?: boolean;
}

export const SupervisorTeamSelect = ({
  teams,
  field,
  form,
  topTeamName,
  multiselect = false,
  permanentlyEnabledTeamIds = [],
  value: propsValue,
  onChange,
  filterMultiValueSelectedIndicators,
  maxVisibleMultiValueCount = 10,
  isDisabled = false,
}: Props) => {
  const scrollPositionRef = useRef<number>(0);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('common');
  const [filteredTeams, setFilteredTeams] = useState<TeamsWithSubTeams>(teams);
  useEffect(() => {
    setFilteredTeams(teams.map((team) => recursiveSearchTeam(team, teams, search, [])).flat(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, teams]);

  const handleChange = (id: string) => {
    if (multiselect) {
      const currentValue = Array.isArray(field.value) ? field.value : [];
      const newValue = currentValue.includes(id)
        ? currentValue.filter((teamId: string) => teamId !== id)
        : [...currentValue, id];

      if (onChange) {
        onChange(newValue);
      } else {
        form.setFieldValue(field.name, newValue);
      }
    } else {
      if (onChange) {
        onChange(id);
      } else {
        form.setFieldValue(field.name, id);
      }
      setIsOpen(false);
      setSearch('');
    }
  };

  const handleSelectWrapperOnChange = (value: { label: string; value: string }[]) => {
    if (multiselect) {
      const newValue = new Set([...permanentlyEnabledTeamIds, ...value.map((item) => item.value)]);

      if (onChange) {
        onChange([...newValue]);
      } else {
        form.setFieldValue(field.name, [...newValue]);
      }
    } else {
      const val = value[0]?.value || (typeof value === 'string' ? value : (value as any).value);
      if (onChange) {
        onChange(val);
      } else {
        form.setFieldValue(field.name, val);
      }
    }
  };

  const recursiveSearchTeam = (
    team: TeamWithSubTeams,
    teams: TeamsWithSubTeams | undefined,
    search: string,
    searchedTeams: TeamsWithSubTeams,
  ) => {
    if (removeDiacritics(team.name?.toLowerCase() || '').includes(removeDiacritics(search.toLowerCase()))) {
      return [...searchedTeams, team];
    } else {
      if (team.subTeams && team.subTeams.length) {
        searchedTeams = team.subTeams
          .map((t) => {
            const foundTeams = recursiveSearchTeam(t, teams, search, searchedTeams).flat();
            return foundTeams;
          })
          .flat();
      }
    }
    return searchedTeams;
  };

  const getSelectedValues = () => {
    const value = propsValue !== undefined ? propsValue : field.value;

    if (multiselect) {
      const teamIds = Array.isArray(value) ? value : [];
      return teamIds
        .map((id: string) => {
          const team = findTeamById(teams, id);
          return { label: team?.name, value: team?.id };
        })
        .filter((item) => item.value !== undefined);
    } else {
      const selectedValue = findTeamById(teams, value);
      return { label: selectedValue?.name, value: selectedValue?.id };
    }
  };

  const [visibleIdsInPreview, firstNonVisibleId, numberOfMoreTeams] = useMemo(() => {
    const selectedValues = getSelectedValues();
    if (Array.isArray(selectedValues)) {
      // if we enable max 5 teams and there is 6 teams, we want to show all six teams, coz we do not want to show +1 more, coz we can just show all of them
      // but when there is 5 teams max and there is 10 teams selected, we want to show only 5 teams and show +5 more
      const selectedValuesDiff = selectedValues.length - maxVisibleMultiValueCount;
      if (selectedValuesDiff > 1) {
        return [
          selectedValues.slice(0, maxVisibleMultiValueCount).map((item) => item.value),
          selectedValues[maxVisibleMultiValueCount]?.value,
          selectedValuesDiff,
        ];
      }
      return [selectedValues.map((item) => item.value), undefined, 0];
    }
    return [selectedValues.value, undefined, 0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsValue, maxVisibleMultiValueCount]);

  return (
    <SelectWrapper
      name={field.name}
      onChange={handleSelectWrapperOnChange}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      onInputChange={(value: string) => {
        setSearch(value);
      }}
      components={{
        Menu: (menuProps: SelectMenuProps) => (
          <CustomSupervisorTeamMenu
            {...menuProps}
            teams={filteredTeams}
            value={propsValue ?? field.value}
            onChange={handleChange}
            search={search}
            ref={scrollPositionRef}
            topTeamName={topTeamName}
            multiselect={multiselect}
            permanentlyEnabledTeamIds={permanentlyEnabledTeamIds}
          />
        ),
        MultiValue: (multiValueProps: any) => {
          if (filterMultiValueSelectedIndicators) {
            const keepValue = filterMultiValueSelectedIndicators(multiValueProps.data.value);
            if (!keepValue) {
              return null;
            }
          }
          if (firstNonVisibleId && multiValueProps.data.value === firstNonVisibleId) {
            return (
              <components.MultiValue {...multiValueProps} data={{ label: '', value: 'more' }}>
                + {numberOfMoreTeams} {t('teams:more')}
              </components.MultiValue>
            );
          }
          if (
            visibleIdsInPreview?.includes(multiValueProps.data.value) ||
            multiValueProps.data.value === SELECT_ALL_ID
          ) {
            return <components.MultiValue {...multiValueProps}>{multiValueProps.children}</components.MultiValue>;
          }
          return null;
        },
        MultiValueRemove: (multiValueRemoveProps: any) => {
          if (
            multiValueRemoveProps.data.value === 'more' ||
            permanentlyEnabledTeamIds.includes(multiValueRemoveProps.data.value)
          ) {
            return null;
          }
          return (
            <components.MultiValueRemove {...multiValueRemoveProps}>
              {multiValueRemoveProps.children}
            </components.MultiValueRemove>
          );
        },
      }}
      isMulti={multiselect}
      value={getSelectedValues()}
      menuIsOpen={isOpen}
      filterOption={(option: unknown, search: string) => setSearch(search)}
      // HACK - filterOption is not called without defined options
      options={[{ value: 'test', label: 'test' }]}
      isDisabled={isDisabled}
      inputValue={search}
      placeholder={`${t('select')}…`}
      styles={{
        container: (provided: React.CSSProperties) => ({
          ...provided,
          width: '100%',
        }),
        control: (provided: React.CSSProperties, state: { isFocused: boolean }) => ({
          ...provided,
          paddingLeft: theme.spacing.f,
          boxShadow: 'none',
          borderColor: state.isFocused
            ? `${theme.colors.borderMain.active} !important`
            : theme.colors.borderMain.default,
        }),
        menu: (provided: React.CSSProperties) => ({
          ...provided,
          borderRadius: '6px',
          fontWeight: `${theme.typography.body.medium.regular.fontWeight}`,
          fontSize: `${theme.typography.body.medium.regular.fontSize}`,
          marginRight: `${theme.spacing.f}`,
          boxShadow: `${theme.shadows.elevationContentDialog}`,
          width: '100%',
          marginTop: '0',
          padding: `${theme.spacing.d} 0`,
        }),
        multiValueLabel: (provided: React.CSSProperties) => ({
          ...provided,
          paddingRight: '6px',
        }),
        multiValueRemove: (provided: React.CSSProperties) => ({
          ...provided,
          paddingLeft: '1px',
        }),
      }}
    />
  );
};

export const findTeamById = (teams: TeamsWithSubTeams, id: string): TeamWithSubTeams | undefined => {
  for (const team of teams) {
    if (team.id === id) {
      return team;
    }
    if (team.subTeams && team.subTeams.length) {
      const foundTeam = findTeamById(team.subTeams, id);
      if (foundTeam) {
        return foundTeam;
      }
    }
  }
  return undefined;
};
